/**
 * One row set, two presentations: a Carbon table at `md` and above, the same
 * rows as tiles below it.
 *
 * The two are mutually exclusive DOM subtrees rather than one tree hidden with
 * CSS — see useIsMobile.ts for why (a `display:none` twin duplicates every
 * row's accessible name, which browsers hide from the accessibility tree but
 * JSDOM does not).
 *
 * This builds on Carbon's presentational `Table*` components rather than its
 * `DataTable` render-prop container. `DataTable` exists to own sorting,
 * filtering and row selection; no page in this app has needed those, and its
 * row model only carries primitives, which would force every cell containing a
 * `<Link>` or a `<Button>` to round-trip through an id lookup. A page that
 * genuinely needs sortable columns should use `DataTable` directly instead of
 * growing this component a sort prop.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tile,
} from '@carbon/react';
import type { ReactNode } from 'react';
import { useIsMobile } from '../../lib/useIsMobile';

export interface Column<T> {
  /** Stable key; also the React key for the cell. */
  key: string;
  /** Column heading, and the label shown beside the value in the tile view. */
  header: string;
  render: (row: T) => ReactNode;
}

export interface ResponsiveDataTableProps<T> {
  /**
   * The table's accessible name. Required: a table with no name is a
   * screen-reader dead end, and this is the one thing a caller cannot default.
   */
  caption: string;
  columns: readonly Column<T>[];
  rows: readonly T[];
  /** Stable identity per row — used as the React key in both presentations. */
  rowId: (row: T) => string;
  /** Rendered in place of both presentations when `rows` is empty. */
  empty?: ReactNode;
}

export function ResponsiveDataTable<T>({
  caption,
  columns,
  rows,
  rowId,
  empty,
}: ResponsiveDataTableProps<T>): React.JSX.Element | null {
  const isMobile = useIsMobile();

  if (rows.length === 0) {
    return empty === undefined ? null : <>{empty}</>;
  }

  if (isMobile) {
    return (
      <ul className="responsive-table__tiles" aria-label={caption}>
        {rows.map((row) => (
          <li key={rowId(row)}>
            <Tile>
              <dl className="responsive-table__pairs">
                {columns.map((column) => (
                  <div key={column.key} className="responsive-table__pair">
                    <dt>{column.header}</dt>
                    <dd>{column.render(row)}</dd>
                  </div>
                ))}
              </dl>
            </Tile>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <TableContainer title={caption}>
      <Table aria-label={caption}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableHeader key={column.key}>{column.header}</TableHeader>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowId(row)}>
              {columns.map((column) => (
                <TableCell key={column.key}>{column.render(row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ResponsiveDataTable;
